/**
 * Intercom Messenger Service
 *
 * Thin wrapper around the global `window.Intercom` queue created by the loader
 * snippet in public/index.html (workspace js75ahv6). Keeps Intercom access in a
 * single, mockable place.
 *
 * The messenger is booted anonymously for every visitor and re-identified once
 * a user authenticates, so support chat is available to both logged-in and
 * anonymous visitors (see JUD-2266).
 *
 * Setup reference:
 * https://app.intercom.com/a/apps/js75ahv6/settings/channels/messenger/install
 */
import config from '../config';

type IntercomCommand = (...args: unknown[]) => void;

declare global {
  interface Window {
    Intercom?: IntercomCommand;
    intercomSettings?: Record<string, unknown>;
  }
}

export interface IntercomUser {
  /** Stable, unique identifier for the authenticated user. */
  userId: string;
  email?: string;
  name?: string;
  userJwt?: string;
}

const isDevelopment = process.env.NODE_ENV === 'development';

const logError = (message: string, error?: unknown): void => {
  // eslint-disable-next-line no-console
  console.error(message, isDevelopment ? error : undefined);
};

const getIntercom = (): IntercomCommand | undefined =>
  typeof window !== 'undefined' ? window.Intercom : undefined;

/**
 * Boot the Intercom messenger as an anonymous visitor.
 *
 * Safe to call before the widget script has finished loading — the snippet
 * queues the call. Intercom creates a "lead" whose conversation can later be
 * merged into a user via {@link identify}.
 */
export const bootAnonymous = (): void => {
  try {
    const intercom = getIntercom();
    if (!intercom) {
      return;
    }
    intercom('boot', { app_id: config.intercom.appId });
  } catch (error) {
    // Intercom must never break the app.
    logError('Failed to boot Intercom messenger', error);
  }
};

/**
 * Associate the current Intercom session with an authenticated user so the
 * conversation is tied to their profile.
 *
 * Uses `update` (rather than shutdown + reboot) so any conversation started
 * while anonymous in the same session is preserved and merged into the user.
 */
export const identify = (user: IntercomUser): void => {
  try {
    const intercom = getIntercom();
    if (!intercom) {
      return;
    }
    intercom('update', {
      app_id: config.intercom.appId,
      user_id: user.userId,
      email: user.email,
      name: user.name,
      ...(user.userJwt ? { intercom_user_jwt: user.userJwt } : {}),
    });
  } catch (error) {
    logError('Failed to identify user with Intercom', error);
  }
};

/**
 * End the current Intercom session and clear its data.
 *
 * Called on logout so the next user on the same browser does not inherit the
 * previous user's conversation.
 */
export const shutdownIntercom = (): void => {
  try {
    const intercom = getIntercom();
    if (!intercom) {
      return;
    }
    intercom('shutdown');
  } catch (error) {
    logError('Failed to shut down Intercom messenger', error);
  }
};
